import React from 'react';
import { connect } from 'react-redux';
import { noop } from 'lodash';
import { bindActionCreators } from 'redux';

import DndContext from 'components/dnd/DndContext.component';
import DndDraggable from 'components/dnd/DndDraggable.component';
import DndDroppable from 'components/dnd/DndDroppable.component';
import Button from 'components/form/Button.component';
import CategoryFieldContainer from 'components/onboarding/CategoryField.component';
import EditCategory from 'components/onboarding/EditCategory.component';
import TooltipForReceptionist from 'components/TooltipForReceptionist';
import LSK from 'constants/core/localStorageKey.const';
import DELAY from 'constants/delay.const';
import DND from 'constants/dnd.const';
import IN from 'constants/in';
import { NO, NOT } from 'constants/no';
import { withTranslation } from 'providers/GILT.provider';
import { selectCurrentSalonId, selectCurrentSalonServices } from 'selectors/currentSalon.selectors';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromArrayToCommatose from 'utils/converters/string/fromArrayToCommatose.util';
import lsGet from 'utils/dom/storage/lsGet.util';
import lsSet from 'utils/dom/storage/lsSet.util';
import delayed from 'utils/functions/delayed.util';
import logger from 'utils/logger.util';
import parseJson from 'utils/parsers/parseJson';
import byId from 'utils/predicates/byId.util';
import dnd from 'utils/react/dnd.util';

import { changeCategoryPosition, changeServicePosition, saveServicesPositions } from 'onboarding/services/actions';
import CategoryItem from 'onboarding/services/CategoryItem';
import SettingsSearchServices from 'settings/services/SettingsSearchServices.component';

import CN from './CategoriesScreen.module.scss';

const L = logger('CategoriesScreen');

const initCategory = () => ({
  color: '#3DA49D',
  name: '',
  services: [],
});

export class CategoriesScreen extends React.Component {
  static defaultProps = {
    onOpenClick: noop,
    onSaveNewCategoryBtnClick: noop,
    isAddCategoryExpanded: false,
    isPostRequestSent: false,
  };

  constructor(props) {
    super(props);

    const expandedServicesIDsStr = L.debug('constructor()', 'expandedServicesIDsStr:', lsGet(LSK.expandedServIds));

    const expandedServicesIDs = expandedServicesIDsStr ? expandedServicesIDsStr.split(',').map((e) => parseInt(e, 10)) : [];

    this.state = {
      expandedAddServiceMap: [],
      expandedServicesIDs,
      editedCategories: props.categories.map((obj) => Object.assign({}, obj)),
      categoryToBeAdded: initCategory(),
      idOfCategoryThatIsAddingService: -1,
      isSearching: false,
    };

    L.debug('constructor()', { props, state: this.state });
  }

  // noinspection JSCheckFunctionSignatures
  UNSAFE_componentWillReceiveProps(nextProps) {
    L.debug('componentWillReceiveProps()', { nextProps, props: this.props, state: this.state });

    const { expandedAddServiceMap: expandedMapFromState, editedCategories: editedCategoriesFromState, idOfCategoryThatIsAddingService } = this.state;

    const editedCategories = nextProps.categories.filter((cat) => editedCategoriesFromState.indexOf(cat.id) < 0).map((obj) => Object.assign({}, obj));

    this.setState({
      editedCategories: L.debug('componentWillReceiveProps()', 'EC:', editedCategories),
    });

    if (idOfCategoryThatIsAddingService !== NO.id && nextProps.isAddNewServicePostRequestSaved) {
      const expandedAddServiceMap = expandedMapFromState.slice(0);
      expandedAddServiceMap[idOfCategoryThatIsAddingService] = false;
      this.setState({ idOfCategoryThatIsAddingService: NO.id, expandedAddServiceMap });
    }
  }

  onCategoryEditHandler = (category) => {
    this.props.onEditClick(category);
  };

  onCategoryDeleteHandler = (category) => {
    this.props.onDeleteClick(category);
  };

  onCategorySaveHandler = (category) => {
    const currentEditedCategory = this.state.editedCategories.find((cat) => cat.id === category.id);
    if (currentEditedCategory) {
      this.props.onSaveClick(currentEditedCategory);
    }
  };

  onCategoryItemEditClick = (service, category) => {
    const expandedServicesIDs = this.state.expandedServicesIDs.slice(0);

    if (!category.id || !service.id) {
      return;
    }
    const srvsIndx = expandedServicesIDs.indexOf(service.id);

    if (srvsIndx === -1 && service.id) {
      expandedServicesIDs.push(service.id);
    } else {
      expandedServicesIDs.splice(srvsIndx, 1);
    }

    const expandedServicesIDsStr = L.debug('onCategoryItemEditClick()', 'expandedServicesIDsStr:', fromArrayToCommatose(expandedServicesIDs));
    lsSet(LSK.expandedServIds, expandedServicesIDsStr);
    this.setState({ expandedServicesIDs });
  };

  onAddNewCategoryBtnClick = () => {
    if (this.props.onSaveNewCategoryBtnClick) {
      this.props.onSaveNewCategoryBtnClick(this.state.categoryToBeAdded);
    }
    this.setState({ categoryToBeAdded: initCategory() });
  };

  onAddNewCategoryNameChange = (name) => {
    const categoryToBeAdded = Object.assign({}, this.state.categoryToBeAdded);
    categoryToBeAdded.name = name;
    this.setState({ categoryToBeAdded });
  };

  onAddNewCategoryColorChange = (color) => {
    const categoryToBeAdded = Object.assign({}, this.state.categoryToBeAdded);
    categoryToBeAdded.color = color;
    this.setState({ categoryToBeAdded });
  };

  onDragEnd = (ev) => {
    // dropped outside the list
    if (!ev?.destination) return;

    const { categories } = this.props;

    const draggable = parseJson(ev?.draggableId);
    const droppable = parseJson(ev?.destination?.droppableId);

    const { type: gType, categoryId: gCatId, serviceId: gServiceId } = draggable ?? {};
    const { type: pType, categoryId: pCatId, serviceId: pServiceId } = droppable ?? {};

    const srcIndex = ev?.source?.index;
    const dstIndex = ev?.destination?.index;

    if (gType === DND.dragService && pType === DND.dropService && gCatId === pCatId && gServiceId !== pServiceId) {
      const foundCat = categories.find(({ id }) => id === gCatId);
      if (foundCat) {
        const foundServices = foundCat.services;
        this.changeServicePosition(foundServices[srcIndex], foundServices[dstIndex]);
      }
    } else if (gType === DND.dragCategory && pType === DND.dropCategory && srcIndex !== dstIndex) {
      this.changeCategoryPosition(categories[srcIndex], categories[dstIndex]);
    }
  };

  changeCategoryName = (name, categoryId) => {
    const editedCategories = this.state.editedCategories.slice(0);
    const currentCategory = editedCategories.find((cat) => cat.id === categoryId);

    if (currentCategory) currentCategory.name = name;

    this.setState({ editedCategories });
  };

  changeCategoryColor = (color, categoryId) => {
    const editedCategories = this.state.editedCategories.slice(0);
    const currentCategory = editedCategories.find((cat) => cat.id === categoryId);

    if (currentCategory) currentCategory.color = color;

    this.setState({ editedCategories });
  };

  changeServicePosition = (serviceToInsert, serviceAt) => {
    L.debug('changeServicePosition()', { serviceToInsert, serviceAt });

    const { saveServicesPositions, salonId, changeServicePosition, categories } = this.props;
    changeServicePosition(serviceToInsert, serviceAt);
    const cat = categories.find(({ id }) => id === serviceToInsert.category);
    if (cat) {
      saveServicesPositions(
        salonId,
        cat.id || 0,
        cat.services.map(({ id }) => id || 0),
      );
    }
  };

  changeCategoryPosition = (categoryToInsert, categoryAt) => {
    L.debug('changeCategoryPosition()', { categoryToInsert, categoryAt });

    this.props.changeCategoryPosition(categoryToInsert, categoryAt);
  };

  handleSelectedSearchedItem = (item) => {
    const category = this.props.categories.find(({ id }) => id === item.category);

    this.props.onOpenClick(category);
    this.onCategoryEditHandler(category);
    //As UNSAFE_componentWillReceiveProps is used in child, it needs to be delayed to update the service roles.
    delayed(DELAY.flash, () => this.onCategoryItemEditClick(item, category));
  };

  setSearching = (isSearching) => void this.setState({ isSearching });

  render() {
    const { expandedAddServiceMap, editedCategories, expandedServicesIDs, isSearching } = this.state;
    const {
      savedCategoriesIDs,
      onOpenClick,
      onEditServiceSave,
      onDeleteServiceClick,
      editingCategoriesIDs,
      salonType,
      salonId,
      saveServicesPositions,
      errorCategoriesData,
      changeCategoryPosition,
      changeServicePosition,
      onAddNewService,
      isAddNewServicePostRequestSent,
      errorServicesData,
      savedServicesIDs,
      expandedCategoriesIDs,
      categories: categoriesFromProps,
      services,
      isOnboarding,
      t,
    } = this.props;

    const handleDragEnd = (ev) => this.onDragEnd(ev);

    // NOTE: checks if the id of the current category is in the expandedCategoriesIDs array.
    // if any of the categories has an id that matches any of the expandedCategoriesIDs then
    // anyExpanded will be true and other categories will be hidden/false.
    const anyExpanded = asArray(categoriesFromProps).some((category) => expandedCategoriesIDs.indexOf(category.id) !== NOT.found);

    const hasCategories = fromAnyToBool(categoriesFromProps && categoriesFromProps.length > 0);

    L.debug('render()', { hasCategories, categoriesFromProps }, { props: this.props, state: this.state });

    return (
      <section data-bem="CategoriesScreen">
        {!anyExpanded && <SettingsSearchServices services={services} onSelect={this.handleSelectedSearchedItem} isSearching={isSearching} onStartSearching={this.setSearching} />}
        {!isSearching && (
          <DndContext tag="CategoriesScreen--dnd-context" onDragEnd={handleDragEnd}>
            <div className="content auto-height">
              <label htmlFor="rankSelector">
                <div className={CN.subTitle}>{t('composite.categoriesScreen.title')}</div>
                <DndDroppable tag="CategoriesScreen--dnd-target" droppableId={dnd.dropCategory()} type="CATEGORIES">
                  {({ innerRef, placeholder }) => (
                    <div data-bem="CategoriesScreen__rank-selector--el-target" id="rankSelector" ref={innerRef}>
                      {placeholder}
                      {hasCategories && (
                        <div data-bem="CategoriesScreen__categories">
                          {categoriesFromProps.map((category, i) => {
                            const categoryId = category.id;
                            const expanded = expandedCategoriesIDs.indexOf(categoryId) !== NOT.found;
                            const editing = editingCategoriesIDs.indexOf(categoryId) !== NOT.found;
                            const saved = savedCategoriesIDs.indexOf(categoryId) !== NOT.found;
                            const editingCategory = editedCategories.find(byId(categoryId));
                            const errorData = errorCategoriesData.find(byId(categoryId));

                            return !anyExpanded || expanded ? (
                              <DndDraggable tag="CategoriesScreen--dnd-source" key={categoryId} draggableId={dnd.dragCategory({ categoryId })} index={i} type="CATEGORIES">
                                {({ dragHandleProps, draggableProps, innerRef }) => (
                                  <div data-bem="CategoriesScreen__category--el-source" ref={innerRef} {...draggableProps} {...dragHandleProps} style={draggableProps.style}>
                                    <div className="relative">
                                      <CategoryFieldContainer
                                        isOnboarding={isOnboarding}
                                        category={category}
                                        expanded={expanded}
                                        onOpenHandler={(clickedCat) => {
                                          if (onOpenClick) {
                                            onOpenClick(clickedCat);
                                            // remove all expanded services ids that are part of this category
                                            const filteredIDs = expandedServicesIDs.filter((expandedId) => !clickedCat.services.find(({ id }) => id === expandedId));
                                            this.setState(
                                              {
                                                expandedServicesIDs: L.debug('render() > map() > filter()', 'filteredIDs:', filteredIDs),
                                              },
                                              () => {
                                                const expandedServicesIDsStr = L.debug('render() > map() > filter()', 'expandedServicesIDsStr:', fromArrayToCommatose(expandedServicesIDs));
                                                lsSet(LSK.expandedServIds, expandedServicesIDsStr);
                                              },
                                            );
                                          }
                                        }}
                                        changeCategoryPosition={(categoryToInsert, categoryAt) => {
                                          changeCategoryPosition(categoryToInsert, categoryAt);
                                        }}
                                        onEditHandler={this.onCategoryEditHandler}
                                        onDeleteHandler={this.onCategoryDeleteHandler}
                                        onSaveHandler={this.onCategorySaveHandler}
                                        isSaved={saved}
                                        errorMessage={errorData ? errorData.message : ''}
                                      />
                                      <TooltipForReceptionist where={IN.services} />
                                    </div>
                                    {editingCategory && editing && (
                                      <EditCategory
                                        category={editingCategory}
                                        changeCategoryName={(name) => {
                                          if (categoryId) {
                                            this.changeCategoryName(name, categoryId);
                                          }
                                        }}
                                        changeCategoryColor={(color) => {
                                          if (categoryId) {
                                            this.changeCategoryColor(color, categoryId);
                                          }
                                        }}
                                      />
                                    )}
                                    {expanded && (
                                      <CategoryItem
                                        category={category}
                                        changeServicePosition={(serviceToInsert, serviceAt) => {
                                          changeServicePosition(serviceToInsert, serviceAt);
                                          const cat = categoriesFromProps.find(({ id }) => id === serviceToInsert.category);
                                          if (cat) {
                                            saveServicesPositions(
                                              salonId,
                                              cat.id || 0,
                                              cat.services.map((service) => service.id || 0),
                                            );
                                          }
                                        }}
                                        dragAndDropEnabled
                                        expandedServicesIDs={expandedServicesIDs}
                                        salonType={salonType}
                                        settingsSavedServicesIDs={savedServicesIDs}
                                        settingsErrorServicesIDs={errorServicesData}
                                        isAddNewServiceExpanded={categoryId ? expandedAddServiceMap[categoryId] : false}
                                        onAddNewService={(service, cat) => {
                                          if (cat.id) {
                                            this.setState({ idOfCategoryThatIsAddingService: cat.id });
                                          }
                                          onAddNewService(service, cat);
                                        }}
                                        navigateToAddService={(cat) => {
                                          const newMap = expandedAddServiceMap.slice(0);
                                          if (cat.id) {
                                            newMap[cat.id] = true;
                                          }
                                          this.setState({ expandedAddServiceMap: newMap });
                                        }}
                                        removeAddService={(cat) => {
                                          const newMap = expandedAddServiceMap.slice(0);
                                          if (cat.id) {
                                            newMap[cat.id] = false;
                                          }
                                          this.setState({ expandedAddServiceMap: newMap });
                                        }}
                                        isPostRequestSent={isAddNewServicePostRequestSent}
                                        isExpandable
                                        onEdit={this.onCategoryItemEditClick}
                                        onDelete={onDeleteServiceClick}
                                        onEditServiceSave={(service, differentPricesRolesIds) => {
                                          this.onCategoryItemEditClick(service, category);
                                          onEditServiceSave(service, differentPricesRolesIds);
                                        }}
                                      />
                                    )}
                                  </div>
                                )}
                              </DndDraggable>
                            ) : null;
                          })}
                        </div>
                      )}

                      {!anyExpanded && (
                        <div className={CN.buttonContainer}>
                          <Button variant="primaryForce" onClick={this.props.onAddNewCategoryClick}>
                            <span className={CN.buttonGap}>{t('composite.categoriesScreen.newCatButton')}</span>
                          </Button>
                          <TooltipForReceptionist where={IN.btnAddJob} />
                        </div>
                      )}

                      {this.props.isAddCategoryExpanded && (
                        <div>
                          <EditCategory category={this.state.categoryToBeAdded} changeCategoryName={this.onAddNewCategoryNameChange} changeCategoryColor={this.onAddNewCategoryColorChange}>
                            <Button variant="primaryForce" onClick={this.onAddNewCategoryBtnClick} disabled={this.props.isPostRequestSent || this.state.categoryToBeAdded.name.length === 0}>
                              <span className={CN.buttonGap}>{t('composite.categoriesScreen.saveCatButton')}</span>
                            </Button>
                          </EditCategory>
                        </div>
                      )}
                    </div>
                  )}
                </DndDroppable>
              </label>
            </div>
          </DndContext>
        )}
      </section>
    );
  }
}

export const mapStateToProps = (state) => ({
  categories: state.onboarding.services.categories,
  salonId: selectCurrentSalonId(state),
  services: selectCurrentSalonServices(state),
  salonType: state.currentSalon ? state.currentSalon.pricing_type : null,
  isAddNewServicePostRequestSent: state.onboarding.services.isPosting,
  isAddNewServicePostRequestSaved: state.onboarding.services.isSaved,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      changeServicePosition,
      saveServicesPositions,
      changeCategoryPosition,
    },
    dispatch,
  );

const CategoriesScreenHoc = connect(mapStateToProps, mapDispatchToProps)(withTranslation()(CategoriesScreen));

export default CategoriesScreenHoc;
